import React, { useState } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    Stepper, Step, StepLabel, Box, TextField, Select, MenuItem, 
    FormControl, InputLabel, Typography, IconButton, Grid, 
    CircularProgress, Avatar, Paper, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { OtaService } from '../api/services/otaService';
import { MediaService } from '../api/services/mediaService';
import { toast } from '../utils/toast';

const DEFAULT_FORM = {
    name: '',
    logo: '',
    config: {
        apiKey: '',
        channelCode: ''
    },
    roomMappings: [],
    rateMappings: []
};

export default function OTAChannelOnboardingModal({ open, onClose, onSuccess, roomTypes = [], ratePlans = [] }) {
    const [activeStep, setActiveStep] = useState(0);
    const [isConnecting, setIsConnecting] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    React.useEffect(() => {
        if (!open) {
            setFormData(DEFAULT_FORM);
            setActiveStep(0);
        }
    }, [open]);

    const steps = ['Platform Configuration', 'Room Mapping', 'Rate Plan Mapping'];

    const handleNext = () => {
        // Simple validation before proceeding
        if (activeStep === 0) {
            if (!formData.name || !formData.config.apiKey || !formData.config.channelCode) {
                toast.error("Please fill all required core configuration fields.");
                return;
            }
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Step 1: Validate MIME type is PNG/JPEG from browser context
        if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
            toast.error("Upload fails; only PNG/JPG accepted");
            return;
        }

        // Step 2: Validate magic numbers/signature to prevent executable/renamed uploads
        const signatureValid = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = (event) => {
                if (event.target.readyState !== FileReader.DONE) {
                    resolve(false);
                    return;
                }
                const arr = new Uint8Array(event.target.result);
                if (arr.length < 2) {
                    resolve(false);
                    return;
                }
                let header = "";
                for (let i = 0; i < Math.min(arr.length, 12); i++) {
                    header += arr[i].toString(16).padStart(2, '0').toUpperCase();
                }

                // Check for executable header (MZ - 4D 5A)
                if (header.startsWith("4D5A")) {
                    resolve(false);
                    return;
                }

                // Verify standard PNG/JPEG magic numbers
                const isPngOrJpg = 
                    header.startsWith("89504E47") || // PNG
                    header.startsWith("FFD8FF");     // JPEG

                resolve(isPngOrJpg);
            };
            reader.readAsArrayBuffer(file.slice(0, 12));
        });

        if (!signatureValid) {
            toast.error("Upload fails; only PNG/JPG accepted");
            return;
        }

        try {
            toast.success("Uploading logo...");
            const response = await MediaService.uploadFile(file);
            const logoUrl = response.url || response;
            setFormData(prev => ({ ...prev, logo: logoUrl }));
            toast.success("Logo uploaded successfully!");
        } catch (error) {
            console.error("Logo upload failed", error);
            toast.error("Logo upload failed");
        }
    };

    const handleConfigChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            config: { ...prev.config, [name]: value }
        }));
    };

    // --- Room Mappings ---
    const addRoomMappingRow = () => {
        setFormData(prev => ({
            ...prev,
            roomMappings: [...prev.roomMappings, { roomTypeId: '', otaRoomCategoryId: '', otaRoomCategoryName: '' }]
        }));
    };

    const removeRoomMappingRow = (index) => {
        setFormData(prev => {
            const newMappings = [...prev.roomMappings];
            newMappings.splice(index, 1);
            return { ...prev, roomMappings: newMappings };
        });
    };

    const handleRoomMappingChange = (index, field, value) => {
        setFormData(prev => {
            const newMappings = [...prev.roomMappings];
            newMappings[index][field] = value;
            return { ...prev, roomMappings: newMappings };
        });
    };

    // --- Rate Mappings ---
    const addRateMappingRow = () => {
        setFormData(prev => ({
            ...prev,
            rateMappings: [...prev.rateMappings, { roomTypeId: '', ratePlanId: '', markupType: 'percentage', markupValue: 0 }]
        }));
    };

    const removeRateMappingRow = (index) => {
        setFormData(prev => {
            const newMappings = [...prev.rateMappings];
            newMappings.splice(index, 1);
            return { ...prev, rateMappings: newMappings };
        });
    };

    const handleRateMappingChange = (index, field, value) => {
        setFormData(prev => {
            const newMappings = [...prev.rateMappings];
            newMappings[index][field] = value;
            return { ...prev, rateMappings: newMappings };
        });
    };

    const handleSubmit = async () => {
        setIsConnecting(true);
        try {
            const payload = JSON.parse(JSON.stringify(formData));
            
            // Recursively remove empty strings and nulls
            const cleanObject = (obj) => {
                Object.keys(obj).forEach(key => {
                    if (obj[key] === '' || obj[key] === null || obj[key] === undefined) {
                        delete obj[key];
                    } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                        cleanObject(obj[key]);
                        if (Object.keys(obj[key]).length === 0) delete obj[key];
                    }
                });
            };
            cleanObject(payload);
            
            // Clean arrays individually
            if (payload.roomMappings) {
                payload.roomMappings.forEach(cleanObject);
            }
            if (payload.rateMappings) {
                payload.rateMappings.forEach(cleanObject);
            }

            await OtaService.connectChannel(payload);
            toast.success("New OTA channel sync established and mapped successfully");
            
            // On Success Reset
            setFormData(DEFAULT_FORM);
            setActiveStep(0);
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            // Interceptor handles toast error logging
            console.error("Failed to connect channel", error);
        } finally {
            setIsConnecting(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Platform Profile</Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth required size="small"
                                    label="Channel Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g. Airbnb"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {formData.logo && (
                                    <Avatar src={formData.logo} variant="rounded" sx={{ width: 40, height: 40 }} />
                                )}
                                <Button variant="outlined" component="label" size="small">
                                    Upload Logo
                                    <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                                </Button>
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" gutterBottom>API Credentials (Config)</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth required size="small"
                                    label="API Key" name="apiKey"
                                    value={formData.config.apiKey}
                                    onChange={handleConfigChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth required size="small"
                                    label="Channel Code" name="channelCode"
                                    value={formData.config.channelCode}
                                    onChange={handleConfigChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Inventory Grid Room Mapping</Typography>
                        {formData.roomMappings.map((mapping, index) => (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={index}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Internal Room Type</InputLabel>
                                            <Select
                                                label="Internal Room Type"
                                                value={mapping.roomTypeId}
                                                onChange={(e) => handleRoomMappingChange(index, 'roomTypeId', e.target.value)}
                                            >
                                                {roomTypes.length > 0 ? roomTypes.map(rt => (
                                                    <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                                                )) : (
                                                    <MenuItem disabled value="">No Room Types Available</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth size="small"
                                            label="OTA Category ID"
                                            value={mapping.otaRoomCategoryId}
                                            onChange={(e) => handleRoomMappingChange(index, 'otaRoomCategoryId', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth size="small"
                                            label="OTA Category Name"
                                            value={mapping.otaRoomCategoryName}
                                            onChange={(e) => handleRoomMappingChange(index, 'otaRoomCategoryName', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1} textAlign="center">
                                        <IconButton color="error" onClick={() => removeRoomMappingRow(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addRoomMappingRow}>
                            Add Room Mapping Row
                        </Button>
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Financial Pricing Rate Mapping</Typography>
                        {formData.rateMappings.map((mapping, index) => (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={index}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Room Type</InputLabel>
                                            <Select
                                                label="Room Type"
                                                value={mapping.roomTypeId}
                                                onChange={(e) => handleRateMappingChange(index, 'roomTypeId', e.target.value)}
                                            >
                                                {roomTypes.length > 0 ? roomTypes.map(rt => (
                                                    <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                                                )) : (
                                                    <MenuItem disabled value="">No Room Types Available</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Rate Plan</InputLabel>
                                            <Select
                                                label="Rate Plan"
                                                value={mapping.ratePlanId}
                                                onChange={(e) => handleRateMappingChange(index, 'ratePlanId', e.target.value)}
                                            >
                                                {ratePlans.length > 0 ? ratePlans.map(rp => (
                                                    <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>
                                                )) : (
                                                    <MenuItem disabled value="">No Rate Plans Available</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Markup Type</InputLabel>
                                            <Select
                                                label="Markup Type"
                                                value={mapping.markupType}
                                                onChange={(e) => handleRateMappingChange(index, 'markupType', e.target.value)}
                                            >
                                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                                <MenuItem value="fixed">Fixed Amount</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth size="small" type="number"
                                            label="Offset Value"
                                            value={mapping.markupValue}
                                            onChange={(e) => handleRateMappingChange(index, 'markupValue', Number(e.target.value))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1} textAlign="center">
                                        <IconButton color="error" onClick={() => removeRateMappingRow(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addRateMappingRow}>
                            Add Rate Mapping Row
                        </Button>
                    </Box>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Dialog open={open} onClose={() => !isConnecting && onClose()} maxWidth="md" fullWidth>
            {isConnecting && (
                <Box 
                    sx={{ 
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                        bgcolor: 'rgba(255,255,255,0.7)', zIndex: 10,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                    }}
                >
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>Establishing Handshake...</Typography>
                    <Typography variant="body2" color="text.secondary">Connecting to OTA network</Typography>
                </Box>
            )}
            <DialogTitle>
                Connect New OTA Channel
                <Typography variant="caption" display="block" color="text.secondary">
                    Follow the wizard to securely connect your inventory.
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ minHeight: 400 }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                
                {renderStepContent(activeStep)}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button 
                    onClick={onClose} 
                    disabled={isConnecting} 
                    color="inherit"
                >
                    Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        disabled={activeStep === 0 || isConnecting} 
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleSubmit} 
                            disabled={isConnecting}
                        >
                            Connect Channel
                        </Button>
                    ) : (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleNext}
                            disabled={isConnecting}
                        >
                            Continue
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
}
